import { useRef, useState } from 'react';
import {
  Button, Caption1, Field, MessageBar, MessageBarBody, OverlayDrawer, DrawerHeader, DrawerHeaderTitle, DrawerBody, DrawerFooter,
  ProgressBar, Textarea, makeStyles, tokens,
} from '@fluentui/react-components';
import {
  ArrowUploadRegular, CheckmarkCircleFilled, DismissCircleFilled, DismissRegular, DocumentRegular, InfoFilled,
} from '@fluentui/react-icons';
import { CellMono } from '../common';
import {
  formatBytes, registerRelease, uploadPackageForCheck, safeSppkgName, type PackageCheck, type RegisterReleaseBody, type Release, type ReleaseChange,
} from '../../api/releases';
import { buildRegisterBody, checkFailed, packageFileProblem, readPackageFacts } from '../../releases/publishPackage';
import { releaseErrorMessage } from '../../releases/releaseChange';
import { useReleaseMutation } from './useReleases';

const useStyles = makeStyles({
  drop: {
    width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalXXL, border: `2px dashed ${tokens.colorNeutralStroke1}`, borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2, cursor: 'pointer', color: tokens.colorNeutralForeground2,
    ':hover': { border: `2px dashed ${tokens.colorBrandStroke1}` },
  },
  dropOver: { border: `2px dashed ${tokens.colorBrandStroke1}`, backgroundColor: tokens.colorBrandBackground2 },
  fileCard: {
    display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, marginBottom: tokens.spacingVerticalM,
  },
  checks: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  check: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-start' },
  ok: { color: tokens.colorPaletteGreenForeground1, fontSize: '18px', flexShrink: 0 },
  no: { color: tokens.colorPaletteRedForeground1, fontSize: '18px', flexShrink: 0 },
  muted: { color: tokens.colorNeutralForeground3 },
  h3: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, margin: `0 0 ${tokens.spacingVerticalS}` },
});

type Stage =
  | { kind: 'idle' }
  | { kind: 'uploading'; file: File; fraction: number; reading: boolean }
  | { kind: 'checked'; file: File; result: PackageCheck }
  | { kind: 'error'; message: string };

export function PublishDrawer({ productId, productName, currentVersion, releases, onClose, onPublished }: {
  productId: string;
  productName: string;
  currentVersion: string | null;
  releases: Release[];
  onClose: () => void;
  onPublished: (change: ReleaseChange, version: string, madeCurrent: boolean) => void;
}) {
  const s = useStyles();
  const input = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [over, setOver] = useState(false);
  const [notes, setNotes] = useState('');
  const register = useReleaseMutation(productId, (v: RegisterReleaseBody) => registerRelease(productId, v));
  const draft = releases.find((r) => r.status === 'Draft');
  const busy = stage.kind === 'uploading' || register.isPending;

  const start = async (file: File | undefined) => {
    if (!file) return;
    register.reset();
    const problem = packageFileProblem(file);
    if (problem) { setStage({ kind: 'error', message: problem }); return; }
    setStage({ kind: 'uploading', file, fraction: 0, reading: false });
    try {
      const result = await uploadPackageForCheck(productId, file, (fraction, done) =>
        setStage({ kind: 'uploading', file, fraction, reading: done }));
      const existing = result.version ? releases.find((r) => r.version === result.version && r.status === 'Draft') : undefined;
      setNotes(existing?.notes ?? '');
      setStage({ kind: 'checked', file, result });
    } catch (e) {
      setStage({ kind: 'error', message: releaseErrorMessage(e) });
    }
  };

  const checked = stage.kind === 'checked' ? stage.result : null;
  const failed = checked ? checkFailed(checked) : false;
  const facts = checked && stage.kind === 'checked' ? readPackageFacts(checked, stage.file.size) : null;
  const canSave = !!checked && !failed && notes.trim().length > 0 && !register.isPending;
  const save = (makeCurrent: boolean) => {
    const body = checked ? buildRegisterBody(checked, notes, makeCurrent) : null;
    if (!body) return;
    register.mutate(body, { onSuccess: (r) => onPublished(r, body.version, makeCurrent) });
  };

  return (
    <OverlayDrawer open position="end" size="medium" onOpenChange={(_, d) => { if (!d.open && !busy) onClose(); }}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={<Button appearance="subtle" aria-label="Close" icon={<DismissRegular />} onClick={onClose} disabled={busy} />}
        >
          Publish new release
        </DrawerHeaderTitle>
        <Caption1 className={s.muted}>{productName}</Caption1>
      </DrawerHeader>
      <DrawerBody>
        <input ref={input} type="file" accept=".sppkg" hidden onChange={(e) => { void start(e.target.files?.[0]); e.target.value = ''; }} />

        {stage.kind === 'idle' || stage.kind === 'error' ? (
          <>
            <p style={{ marginTop: 0 }} className={s.muted}>
              Current release: <b>{currentVersion ?? 'none'}</b>{draft ? <> · Draft: <b>{draft.version}</b></> : null}
            </p>
            {stage.kind === 'error' && <MessageBar intent="error" style={{ marginBottom: 12 }}><MessageBarBody>{stage.message}</MessageBarBody></MessageBar>}
            <button
              type="button" className={`${s.drop} ${over ? s.dropOver : ''}`}
              onClick={() => input.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setOver(true); }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => { e.preventDefault(); setOver(false); void start(e.dataTransfer.files?.[0]); }}
            >
              <ArrowUploadRegular fontSize={28} />
              <b>Drop the .sppkg here</b>
              <span>or click to choose a file</span>
            </button>
            <p className={s.muted}>Everything in the check is read from the package itself. Nobody types a version or an id.</p>
          </>
        ) : (
          <>
            <div className={s.fileCard}>
              <DocumentRegular fontSize={24} />
              <div>
                <b>{safeSppkgName(stage.file.name) ?? stage.file.name}</b>
                <div className={s.muted}>{formatBytes(stage.file.size)}{stage.kind === 'checked' ? ' · uploaded' : ''}</div>
              </div>
            </div>
            {stage.kind === 'uploading' && (
              <>
                <ProgressBar value={stage.reading ? undefined : stage.fraction} />
                <p className={s.muted}>{stage.reading ? 'Reading the package manifest…' : `Uploading… ${Math.round(stage.fraction * 100)}%`}</p>
              </>
            )}
            {stage.kind === 'checked' && (
              <>
                <h3 className={s.h3}>Package check</h3>
                <ul className={s.checks}>
                  {stage.result.checks.map((c) => (
                    <li key={c.id} className={s.check}>
                      {c.ok ? <CheckmarkCircleFilled className={s.ok} /> : <DismissCircleFilled className={s.no} />}
                      <div><b>{c.label}</b>{c.detail && <div className={s.muted}>{c.detail}</div>}</div>
                    </li>
                  ))}
                  {stage.result.replacesDraft && stage.result.version && (
                    <li className={s.check}><InfoFilled className={s.muted} /><div><b>Existing draft</b><div className={s.muted}>Replaces the {stage.result.version} draft package.</div></div></li>
                  )}
                  {facts && (facts.sizeBytes !== null || facts.sha256Prefix) && (
                    <li className={s.check}>
                      <InfoFilled className={s.muted} />
                      <div><b>Package</b><div className={s.muted}>
                        {facts.sizeBytes !== null ? formatBytes(facts.sizeBytes) : null}
                        {facts.sha256Prefix && <>{facts.sizeBytes !== null ? ' · ' : ''}sha256 <CellMono>{facts.sha256Prefix}…</CellMono></>}
                      </div></div>
                    </li>
                  )}
                </ul>
                {failed && (
                  <MessageBar intent="error" style={{ marginTop: 12 }}>
                    <MessageBarBody>This package cannot be published. Fix the failed checks and upload again.</MessageBarBody>
                  </MessageBar>
                )}
                <Field label="Release notes" required hint="Shown in Guardian and to tenant admins." style={{ marginTop: 16 }}>
                  <Textarea value={notes} onChange={(_, d) => setNotes(d.value)} rows={5} disabled={failed || register.isPending} />
                </Field>
                <p className={s.muted}>Publishing installs nothing. Tenants only move when you roll out.</p>
                {register.error && <MessageBar intent="error"><MessageBarBody>{releaseErrorMessage(register.error)}</MessageBarBody></MessageBar>}
              </>
            )}
          </>
        )}
      </DrawerBody>
      <DrawerFooter>
        {stage.kind !== 'idle' && !busy && <Button appearance="transparent" onClick={() => setStage({ kind: 'idle' })}>Upload a different file</Button>}
        <span style={{ flexGrow: 1 }} />
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button disabled={!canSave} onClick={() => save(false)}>Save as draft</Button>
        <Button appearance="primary" disabled={!canSave} onClick={() => save(true)}>
          {register.isPending ? 'Publishing…' : 'Publish and make current'}
        </Button>
      </DrawerFooter>
    </OverlayDrawer>
  );
}
